import { convertImage, contentResolutionLabel, isDynamicContent } from "../../../../services/contents";
import Colors from "../../../../themes/Colors";

const getVersionNumber = (versionName) => {
  if (!versionName || !versionName.startsWith("V")) return 0;
  const n = parseInt(versionName.substring(1), 10);
  return isNaN(n) ? 0 : n;
};

const sortAds = (list) =>
  list.slice().sort((a, b) => {
    if (a.active === 1 && b.active === 0) return -1;
    if (a.active === 0 && b.active === 1) return 1;
    const va = getVersionNumber(a.content_version_name);
    const vb = getVersionNumber(b.content_version_name);
    if (va !== vb) return va - vb;
    return 0;
  });

export const buildScreenGroups = (campaign) => {
  const groups = {};
  const order = [];

  (campaign.contents_displays || []).forEach((relation) => {
    const key = relation.display_id;
    if (!groups[key]) {
      groups[key] = [];
      order.push(key);
    }

    if (relation.content_type === "smartv2" && relation.content_child_contents) {
      relation.content_child_contents.forEach((child) => {
        groups[key].push({
          ...relation,
          content_name: child.name,
          file: child.file,
          file_thumb: child.file_thumb,
          content_file: child.file,
          content_width: child.width,
          content_height: child.height,
          content_type: child.type,
          length: child.length,
          rules: child.rules,
          max_impressions_per_day: child.max_impressions_per_day,
        });
      });
    } else {
      groups[key].push(relation);
    }
  });

  return order.map((key) => {
    const ads = sortAds(groups[key]);
    return { screen: ads[0], ads };
  });
};

export const getRealShows = (relation, reportSingleData) => {
  if (!reportSingleData || !reportSingleData.report) return null;
  const match = reportSingleData.report.find(
    (el) => el.display === relation.display_id && el.content === relation.content_id
  );
  if (match) return match.shows;
  if (reportSingleData.report.length === 1 && reportSingleData.report[0].display === relation.display_id) {
    return reportSingleData.report[0].shows;
  }
  return null;
};

export const getRevisionTag = (relation, t) => {
  if (relation.revision_status === "approved" && relation.active === 1) {
    return { label: t("Spot Approved"), color: Colors.green };
  }
  if (relation.active === 0 && relation.revision_status === "approved") {
    return { label: t("Paused"), color: Colors.textMuted };
  }
  if (relation.active === 0 && relation.revision_status === "inactive") {
    return { label: t("Inactive"), color: Colors.textMuted };
  }
  if (relation.revision_status === "rejected") {
    return { label: t("Spot Rejected"), color: Colors.red };
  }
  if (relation.revision_status === "pending") {
    return { label: t("The owner of this screen has not yet approved"), color: Colors.atention };
  }
  if (relation.revision_status === "pending_content") {
    return { label: t("On hold"), color: Colors.textMuted };
  }
  return null;
};


const getRawSource = (relation) => {
  if (isDynamicContent(relation.content_type)) {
    return relation.file_thumb || relation.content_file_thumb || null;
  }
  return (
    relation.file_thumb ||
    relation.content_file_thumb ||
    relation.content_file ||
    relation.file ||
    null
  );
};

export const getCreativeThumb = (relation) => {
  const src = getRawSource(relation);
  return src ? convertImage(src) : null;
};

export const getVideoSource = (relation) => getRawSource(relation);

export const isVideoContent = (relation) => relation.content_type === "video";

// "1080 x 1920", or "DCO" for a dynamic ad, which has no resolution of its own.
// The campaign detail carries the content flattened onto the relation, hence the
// content_* names.
export const getAdFormatLabel = (relation, t) =>
  contentResolutionLabel(
    { type: relation.content_type, width: relation.content_width, height: relation.content_height },
    t
  );

export const getAdAspect = (relation) => {
  const w = Number(relation.content_width) || 0;
  const h = Number(relation.content_height) || 0;
  if (!w || !h) return { w: 16, h: 9, orientation: "horizontal" };
  let orientation;
  if (h > w) orientation = "vertical";
  else if (w === h) orientation = "square";
  else if (w / h >= 2.2) orientation = "super-horizontal";
  else orientation = "horizontal";
  return { w, h, orientation };
};

export const isSubsidized = (relation) =>
  relation.paid_amount_with_profit === 0 && relation.promised_shows > 0;

export const formatVersion = (name, t) => {
  if (!name) return null;
  const m = /^V(\d+)$/i.exec(name);
  return m ? `${t("Version")} ${m[1]}` : name;
};

export const parseRules = (relation) => {
  const raw = relation.rules;
  if (!raw) return [];
  let arr;
  try {
    arr = typeof raw === "string" ? JSON.parse(raw) : raw;
  } catch (e) {
    return [];
  }
  if (!Array.isArray(arr)) return [];

  return arr.map((rule) => {
    const conds = (rule && rule.conditions) || [];
    const date = conds.find((c) => c.type === "date");
    const day = conds.find((c) => c.type === "day");
    const hour = conds.find((c) => c.type === "hour");
    return {
      fromDate: date && date.from_date ? date.from_date : relation.from_date || null,
      toDate: date && date.to_date ? date.to_date : relation.to_date || null,
      days: day && Array.isArray(day.value) && day.value.length ? day.value : [1, 2, 3, 4, 5, 6, 0],
      fromHour: hour && hour.from ? hour.from : null,
      toHour: hour && hour.to ? hour.to : null,
    };
  });
};

export const DAY_PILLS = [
  { value: 1, key: "M" },
  { value: 2, key: "T" },
  { value: 3, key: "W" },
  { value: 4, key: "TH" },
  { value: 5, key: "F" },
  { value: 6, key: "SA" },
  { value: 0, key: "SU" },
];
